<!-- components/HashtagDrawer.vue -->
<template>
  <div class="drawer-overlay" @click.self="$emit('close')">
    <div class="drawer-panel">
      <div class="drawer-header">
        <h2 class="drawer-title">Редактировать хэштеги</h2>
        <button class="close-button" @click="$emit('close')">&#10005;</button>
      </div>

      <div class="drawer-content">
        <p class="step-description">
          Добавьте хэштеги, чтобы ваша вакансия лучше находилась пользователями по интересующим темам и навыкам.
        </p>

        <div v-if="selected.length" class="selected-hashtags">
          <div
            v-for="tag in selected"
            :key="tag.id"
            class="selected-hashtag"
          >
            #{{ tag.name }}
            <span class="remove-icon" @click="remove(tag.id)">✖</span>
          </div>
        </div>

        <div class="search-bar-container">
          <div class="search-bar">
            <img :src="searchIconPath" alt="🔍" class="search-icon" />
            <input
              type="text"
              v-model="searchQuery"
              placeholder="Введите хэштег"
              class="search-input"
              @input="searchHashtags"
            />
          </div>
        </div>

        <div v-if="searchQuery.trim().length > 0 && allTags.length > 0" class="hashtags-list">
          <div
            v-for="tag in allTags"
            :key="tag.id"
            class="hashtag"
            :class="{ selected: isSelected(tag.id) }"
            @click="toggle(tag)"
          >
            {{ tag.name }}
          </div>
        </div>
      </div>

      <div class="drawer-footer">
        <button class="save-btn" @click="save">Сохранить</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { debounce } from 'lodash';

const props = defineProps({
  modelValue: Array,
  allTags: Array
});
const emit = defineEmits(['close', 'update:modelValue']);

const selected = ref([...props.modelValue]);
const searchQuery = ref("");
const searchIconPath = ref("");

const updateSearchIcon = () => {
  const theme = document.documentElement.getAttribute("data-theme");
  searchIconPath.value = theme === "light"
    ? new URL("../assets/search_light.png", import.meta.url).href
    : new URL("../assets/search_dark.png", import.meta.url).href;
};

onMounted(() => {
  updateSearchIcon();
  const observer = new MutationObserver(() => updateSearchIcon());
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
});

const isSelected = (id) => selected.value.some(tag => tag.id === id);

const toggle = (tag) => {
  if (isSelected(tag.id)) {
    remove(tag.id);
  } else if (selected.value.length < 5) {
    selected.value.push(tag);
  }
};

const remove = (id) => {
  selected.value = selected.value.filter(t => t.id !== id);
};

const save = () => {
  emit('update:modelValue', selected.value);
  emit('close');
};

const searchHashtags = debounce(() => {}, 500);
</script>

<style scoped>

.drawer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  justify-content: flex-start;
  align-items: stretch;
  z-index: 1000;
}

.drawer-panel {
  width: 900px;
  background-color: var(--drawer-bg, #fff);
  box-shadow: 4px 0 10px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  animation: slideIn 0.25s ease-out;
}

[data-theme="dark"] .drawer-panel {
  background-color: #1f1f1f;
}

@keyframes slideIn {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0);
  }
}

.drawer-header {
  display: flex;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
  position: relative;
}

.drawer-title {
  font-family: "Eastman-Bold";
  font-size: 16px;
  flex: 1;
}

.close-button {
  background: transparent;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: var(--text-primary);
}

.drawer-content {
  flex: 1;
  padding: 40px 30px;
}

.drawer-footer {
  display: flex;
  justify-content: flex-end;
  padding: 16px 30px;
  border-top: 1px solid #eee;
}

.save-btn {
  background-color: #3a82f7;
  color: white;
  padding: 10px 20px;
  border-radius: 12px;
  border: none;
  font-size: 14px;
  font-family: "Eastman-Medium";
  cursor: pointer;
}

.step-description {
  font-family: "Eastman-Regular", sans-serif;
  font-size: 14px;
  color: var(--text-secondary);
  margin-bottom: 16px;
  line-height: 1.5;
}

.selected-hashtags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.selected-hashtag {
  background: #2887F7;
  color: white;
  font-size: 14px;
  font-family: "Eastman-Medium", sans-serif;
  padding: 8px 15px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
}

.remove-icon {
  font-size: 12px;
  color: white;
  cursor: pointer;
  transition: opacity 0.2s ease-in-out;
}
.remove-icon:hover {
  opacity: 0.7;
}

.search-bar-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

.search-bar {
  display: flex;
  align-items: center;
  background-color: var(--hashtag-background-registration-dark);
  border-radius: 30px;
  padding: 10px 15px;
  width: 400px;
}
[data-theme="light"] .search-bar {
  background-color: var(--hashtag-background-registration-light);
}

.search-icon {
  width: 20px;
  height: 20px;
  margin-right: 10px;
}

.search-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  font-size: 13px;
  font-family: "Eastman-Regular", sans-serif;
  color: var(--header-text-dark);
}
[data-theme="light"] .search-input {
  color: var(--header-text-light);
}
.search-input::placeholder {
  color: #B0B0B0;
}

.hashtags-list {
  margin-top: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.hashtag {
  background: #4C4B4B;
  color: white;
  font-size: 14px;
  font-family: "Eastman-Medium", sans-serif;
  padding: 8px 15px;
  border-radius: 20px;
  cursor: pointer;
  transition: background 0.2s ease-in-out, opacity 0.2s ease-in-out;
}
.hashtag.selected {
  background: #2887F7;
}
.hashtag:hover {
  opacity: 0.8;
}

[data-theme="dark"] .drawer-header {
  border-bottom: 1px solid #333; /* тёмный сепаратор */
}

[data-theme="dark"] .drawer-footer {
  border-top: 1px solid #333; /* тёмный сепаратор */
}

</style>